import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Messages: React.FC<{ user: any }> = ({ user }) => {
  const { username } = useParams(); // For specific conversation
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/messages`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setConversations(res.data);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  }, [user.token]);

  const fetchMessages = useCallback(async () => {
    if (!username) return;
    try {
      const res = await axios.get(`${API_URL}/messages/${username}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setMessages(res.data);
      
      // Mark as read
      await axios.put(`${API_URL}/messages/read/${username}`, {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [username, user.token]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchConversations();
      if (username) await fetchMessages();
      setLoading(false);
    };
    loadInitialData();
  }, [username, fetchConversations, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !newMessage.trim()) return;

    try {
      await axios.post(`${API_URL}/messages`, 
        { receiver_username: username, content: newMessage },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setNewMessage('');
      await fetchMessages();
      await fetchConversations();
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const handleSearch = useCallback(async (val: string) => {
    setSearchTerm(val);
    if (val.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/users/search?q=${val}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setSearchResults(res.data);
    } catch (err) {
      console.error('Error searching users:', err);
    }
  }, [user.token]);

  if (loading) return <LoadingSpinner message="Opening the communication channels..." />;

  return (
    <div className="content" style={{ display: 'flex', gap: '2rem', height: 'calc(100vh - 150px)' }}>
      {/* Sidebar: Conversations */}
      <div className="card" style={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--gold)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Conversations</h3>
            <button 
                onClick={() => setShowSearchModal(true)} 
                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', backgroundColor: 'var(--mystic-purple)' }}
            >
                + New
            </button>
        </div>
        
        {/* Search Modal */}
        {showSearchModal && (
            <div className="modal-overlay" onClick={() => setShowSearchModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="modal-content card" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: '400px' }}>
                    <h3>Start New Dialogue</h3>
                    <input 
                        type="text" 
                        placeholder="Search username..." 
                        value={searchTerm}
                        onChange={e => handleSearch(e.target.value)}
                        autoFocus
                    />
                    <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '1rem' }}>
                        {searchResults.map(u => (
                            <div 
                                key={u.username} 
                                onClick={() => { navigate(`/messages/${u.username}`); setShowSearchModal(false); setSearchTerm(''); setSearchResults([]); }}
                                style={{ padding: '0.8rem', borderBottom: '1px solid rgba(212, 175, 55, 0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.8rem' }}
                            >
                                {u.profile_picture ? (
                                    <img src={u.profile_picture} alt={u.username} style={{ width: '30px', height: '30px', borderRadius: '50%' }} />
                                ) : (
                                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'var(--mystic-purple)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>{u.username[0].toUpperCase()}</div>
                                )}
                                <span>{u.display_name || u.username}</span>
                            </div>
                        ))}
                        {searchTerm.length >= 2 && searchResults.length === 0 && <p style={{ opacity: 0.7 }}>No practitioners found.</p>}
                    </div>
                    <button onClick={() => setShowSearchModal(false)} style={{ marginTop: '1rem', width: '100%', backgroundColor: '#444' }}>Close</button>
                </div>
            </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {conversations.length > 0 ? conversations.map((conv: any) => (
            <div 
              key={conv.id} 
              onClick={() => navigate(`/messages/${conv.other_username}`)}
              style={{ 
                padding: '1rem', 
                cursor: 'pointer', 
                borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
                backgroundColor: username === conv.other_username ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '0.8rem'
              }}
            >
              {conv.other_profile_picture ? (
                <img src={conv.other_profile_picture} alt={conv.other_username} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--mystic-purple)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                  {conv.other_username[0].toUpperCase()}
                </div>
              )}
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontWeight: 'bold', color: 'var(--gold)' }}>{conv.other_display_name || conv.other_username}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {conv.content}
                </div>
              </div>
              {conv.is_read === 0 && conv.receiver_id === user.id && (
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--gold)' }}></div>
              )}
            </div>
          )) : (
            <p style={{ padding: '1rem', opacity: 0.7 }}>No conversations yet.</p>
          )}
        </div>
      </div>

      {/* Main: Messages */}
      <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        {username ? (
          <>
            <h3 style={{ padding: '1rem', borderBottom: '1px solid var(--gold)', margin: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Chat with {username}</span>
              <Link to={`/profile/${username}`} style={{ fontSize: '0.8rem', color: 'var(--gold)', textDecoration: 'none', border: '1px solid var(--gold)', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>View Profile</Link>
            </h3>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {messages.map((msg: any) => (
                <div 
                  key={msg.id} 
                  style={{ 
                    maxWidth: '70%', 
                    alignSelf: msg.sender_id === user.id ? 'flex-end' : 'flex-start',
                    backgroundColor: msg.sender_id === user.id ? 'var(--mystic-purple)' : '#2a2a3a',
                    padding: '0.8rem 1rem',
                    borderRadius: '10px',
                    border: msg.sender_id === user.id ? '1px solid var(--gold)' : '1px solid #444',
                    position: 'relative'
                  }}
                >
                  <div style={{ fontSize: '0.9rem', marginBottom: '0.3rem' }}>{msg.content}</div>
                  <div style={{ fontSize: '0.6rem', opacity: 0.5, textAlign: 'right' }}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <form onSubmit={handleSendMessage} style={{ padding: '1rem', borderTop: '1px solid var(--gold)', display: 'flex', gap: '0.5rem' }}>
              <input 
                type="text" 
                placeholder="Type your mystical message..." 
                value={newMessage} 
                onChange={e => setNewMessage(e.target.value)}
                style={{ marginBottom: 0 }}
                required
              />
              <button type="submit" style={{ width: 'auto', padding: '0 1.5rem' }}>Send</button>
            </form>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
            <p>Select a conversation to begin.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
